import React, { useMemo, useState } from "react";
import { Button, Col, Container, Image, Row } from "react-bootstrap";
import { MdOutlineSearch } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../context";
import { Toaster } from "sonner";
import {
  Featured,
  FilterByBrand,
  FilterByCategory,
  FilterBySize,
  Footer,
  Inputs,
  NavLinkWithLocale,
  SearchColorCard,
} from "../../components";
import {
  Alert,
  sponsors,
  useBalloonsV2,
  useFilterByBrand,
  useFilterByCategory,
  useScreenSize,
  useSizeFilter,
} from "../../utils";
import style from "./SearchTool.module.css";

const SearchTool = () => {
  const { user } = useAppContext();
  const screenSize = useScreenSize();
  const [search, setSearch] = useState("");
  const [showBalloons, setShowBalloons] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { t } = useTranslation();
  const { data: balloonsData, isLoading: loadingBalloons } = useBalloonsV2();

  const userPro =
    user?.stripeSubscriptionStatus === "active" ||
    user?.stripeSubscriptionStatus === "trialing";

  const filteredBalloons = balloonsData?.filter((balloon) => {
    // Dividimos la búsqueda en palabras clave
    const searchTerms = search.toLowerCase().trim().split(/\s+/);

    return searchTerms.every((term) => {
      // Expresión regular para buscar la palabra completa
      const regex = new RegExp(`\\b${term}\\b`, "i");

      // Comparamos cada término con las propiedades relevantes
      const nameMatch = regex.test(balloon.name?.toLowerCase() || "");
      const displayNameMatch = regex.test(
        balloon.displayName?.toLowerCase() || ""
      );
      const colorCategoriesMatch = (balloon.colorCategories ?? []).some(
        (category) => regex.test(category?.toLowerCase() || "")
      );

      const brandMatch = balloon.combined
        ? regex.test(balloon.insideBrand?.toLowerCase() || "") ||
          regex.test(balloon.outsideBrand?.toLowerCase() || "")
        : regex.test(balloon.brand?.toLowerCase() || "");

      // El globo solo se muestra si TODOS los términos están en alguna propiedad
      return (
        nameMatch || displayNameMatch || colorCategoriesMatch || brandMatch
      );
    });
  });

  // Aplicación de filtros adicionales
  const { brandsSelected, handleBrandsChange, filteredBrands } =
    useFilterByBrand(filteredBalloons);
  const { sizesSelected, handleSizesChange, filteredSizes } =
    useSizeFilter(filteredBalloons);
  const { categoriesSelected, handleCategoryChange, filteredCategories } =
    useFilterByCategory(filteredBalloons);

  const options = useMemo(() => {
    let result = filteredBalloons;

    const hasBrandFilter = Object.values(brandsSelected).includes(false);
    const hasSizeFilter = Object.values(sizesSelected).includes(false);
    const hasCategoryFilter = Object.values(categoriesSelected).includes(false);

    if (hasBrandFilter) result = filteredBrands;
    if (hasSizeFilter)
      result = result.filter((item) => filteredSizes.includes(item));
    if (hasCategoryFilter)
      result = result.filter((item) => filteredCategories.includes(item));

    return result;
  }, [
    filteredBalloons,
    brandsSelected,
    filteredBrands,
    sizesSelected,
    filteredSizes,
    categoriesSelected,
    filteredCategories,
  ]);

  const handleSearch = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    /* setSearch(value); */
    //setShowBalloons(false);
  };

  const handleOnClickSearch = () => {
    setSearch(searchValue);
    setShowBalloons(true);
    if (!options?.length) {
      return Alert.error(
        t("No results found. Try a different term or adjust your filters.")
      );
    }
  };

  return (
    <>
      <Container fluid>
        <Row
          className={`justify-content-center align-items-center pb-4 pt-4 ${
            !showBalloons ? style.searchBanner : style.searchBannerBeforeSearch
          }`}
        >
          <Col sm={12} md={10} lg={5}>
            <h1>{t("Search Balloons")}</h1>
            <p>
              {t("Your all-in-one reference for top balloon brands")}
              {!showBalloons &&
                t(
                  "—quickly find colors and sizes across leading brands, saving you time and effort."
                )}
            </p>
          </Col>
          <Col sm={12} md={10} lg={8} className="mb-4 px-md-5">
            <Inputs.FormControl
              value={searchValue}
              onChange={handleSearch}
              customClass={style.searchBarLabel}
              className={style.searchBar}
              icon={screenSize > 992 && <MdOutlineSearch />}
              placeholder={t("Search by balloon color or brand...")}
              decorationBtn
              size="lg"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleOnClickSearch();
                }
              }}
              decoration={
                <Button
                  variant="light"
                  className={style.searchBtn}
                  onClick={handleOnClickSearch}
                  size="md"
                >
                  {screenSize > 992 ? t("SEARCH") : <MdOutlineSearch />}
                </Button>
              }
            />
          </Col>
        </Row>
        {!showBalloons && (
          <Row className="justify-content-around mt-4">
            {/* <h2 className={style.sponsorsTitle}>Supported Brands</h2> */}
            {sponsors.map((sponsor) => (
              <Col xs={3} md={2} lg={1} key={sponsor.name}>
                <Image
                  width="100%"
                  src={sponsor.image}
                  alt={sponsor.name}
                  className={style.sponsorImage}
                />
              </Col>
            ))}
          </Row>
        )}
      </Container>
      <Container className="my-5">
        <Row>
          <Col xs={12}>
            {!showBalloons && <Featured />}
            {showBalloons && !loadingBalloons && search !== "" && (
              <p className={`mt-4 ${style.resultsCount}`}>
                {options.length} {t("Results for")} "{search}"
              </p>
            )}
          </Col>
          <Col xs={12}>
            {!loadingBalloons && search !== "" && showBalloons && (
              <Row className="p-0 m-0 justify-content-between justify-content-lg-start">
                <Col xs="auto" className="p-0 pe-lg-3">
                  <FilterByBrand
                    brandsSelected={brandsSelected}
                    handleChange={handleBrandsChange}
                    variant="light"
                    customClass={style.filters}
                  />
                </Col>
                <Col xs="auto" className="p-0 pe-lg-3">
                  <FilterBySize
                    sizesSelected={sizesSelected}
                    handleChange={handleSizesChange}
                    variant="light"
                    customClass={style.filters}
                  />
                </Col>
                <Col xs="auto" className="p-0 pe-lg-3">
                  <FilterByCategory
                    categoriesSelected={categoriesSelected}
                    handleChange={handleCategoryChange}
                    variant="light"
                    customClass={style.filters}
                  />
                </Col>
              </Row>
            )}
            {showBalloons && (
              <Row>
                {!loadingBalloons &&
                  search !== "" &&
                  options.map((balloon) => (
                    <Col key={balloon.id} xs={6} md={4} lg={2} className="my-2">
                      <SearchColorCard key={balloon.color} balloon={balloon} />
                    </Col>
                  ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>
      {!user && (
        <Container fluid className={`py-5 ${style.searchBottomBanner}`}>
          <Row className={`justify-content-around`}>
            <Col xs={12} md={6} className={style.bottomBannerText}>
              <h2>{t("Find your Balloon Match")}</h2>
              <p>
                {t(
                  "Have an inspiration image and need to find the right balloons? Upload a photo, click on a color, and get the closest match in seconds. Try it now!"
                )}
              </p>
            </Col>
            <Col xs="auto" className="mt-sm-5 mt-md-0">
              {!user ? (
                <NavLinkWithLocale to="/createAccount">
                  <Button size="lg" className={style.footerBannerBtn}>
                    {t("SIGN UP FOR FREE")}
                  </Button>
                </NavLinkWithLocale>
              ) : (
                !userPro && (
                  <NavLinkWithLocale to="/createAccount">
                    <Button size="lg" className={style.footerBannerBtn}>
                      {t("SIGN UP FOR PRO")}
                    </Button>
                  </NavLinkWithLocale>
                )
              )}
              <p className={style.announce}>{t("No credit card required.")}</p>
            </Col>
          </Row>
        </Container>
      )}
      <Toaster richColors position="bottom-center" />
      <Footer />
    </>
  );
};

export default SearchTool;
